<template>
  <div class="settings-page">
    <main class="settings-shell">
      <header class="page-header">
        <button class="back-btn" type="button" @click="handleBack">
          <img class="back-icon" src="@/static/icon/left.png" alt="" />
          <span>{{ $t('common.back') }}</span>
        </button>
        <h1>{{ $t('settings.title') }}</h1>
      </header>

      <section class="settings-list">
        <button
          v-for="item in menuItems"
          :key="item.id"
          class="settings-item"
          type="button"
          @click="handleMenuClick(item)"
        >
          <span :class="['item-icon', `item-icon-${item.id}`]" aria-hidden="true">
            <svg v-if="item.id === 'profile'" viewBox="0 0 24 24">
              <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" />
              <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
            </svg>
            <svg v-else-if="item.id === 'password'" viewBox="0 0 24 24">
              <rect x="5" y="10" width="14" height="10" rx="2.5" />
              <path d="M8 10V7a4 4 0 0 1 8 0v3" />
              <path d="M12 14.5v2" />
            </svg>
            <svg v-else viewBox="0 0 24 24">
              <path d="M9 5H6.5A2.5 2.5 0 0 0 4 7.5v9A2.5 2.5 0 0 0 6.5 19H9" />
              <path d="M15 8l4 4-4 4" />
              <path d="M19 12H9" />
            </svg>
          </span>

          <span class="item-content">
            <span class="item-title">{{ $t(item.titleKey) }}</span>
            <span class="item-desc">{{ $t(item.descKey) }}</span>
          </span>

          <span class="chevron">›</span>
        </button>
      </section>
    </main>
  </div>
</template>

<script>
import { Dialog, Toast } from 'vant'

export default {
  name: 'SettingsPage',
  data() {
    return {
      menuItems: [
        {
          id: 'profile',
          titleKey: 'settings.editProfile',
          descKey: 'settings.editProfileDesc',
          path: '/settings/edit-profile'
        },
        {
          id: 'password',
          titleKey: 'settings.changePassword',
          descKey: 'settings.changePasswordDesc',
          path: '/settings/change-password'
        },
        {
          id: 'logout',
          titleKey: 'settings.logout',
          descKey: 'settings.logoutDesc'
        }
      ]
    }
  },
  methods: {
    handleBack() {
      this.$router.back()
    },
    handleMenuClick(item) {
      if (item.id === 'logout') {
        this.confirmLogout()
        return
      }

      this.$router.push(item.path)
    },
    confirmLogout() {
      Dialog.confirm({
        title: this.$t('settings.logoutConfirmTitle'),
        message: this.$t('settings.logoutConfirmMessage'),
        confirmButtonText: this.$t('settings.logoutConfirmAction'),
        cancelButtonText: this.$t('settings.cancel')
      })
        .then(() => {
          localStorage.removeItem('token')
          localStorage.removeItem('account')
          this.$removeUserInfo()
          Toast(this.$t('settings.logoutSuccess'))
          this.$router.replace('/base/login')
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
}

.settings-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at 16% 10%, rgba(255, 255, 255, 0.95), transparent 28%),
    linear-gradient(145deg, #f7f8fb 0%, #eef1f5 48%, #ffffff 100%);
  color: #111111;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif;
}

.settings-shell {
  width: 100%;
  max-width: 450px;
  min-height: 100vh;
  margin: 0 auto;
  padding: 22px 18px 34px;
}

.page-header {
  padding: 4px 0 20px;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #1d1d1f;
  font: inherit;
  font-size: 15px;
  font-weight: 500;
}

.back-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.page-header h1 {
  margin: 18px 0 0;
  color: #111111;
  font-size: 34px;
  font-weight: 750;
  line-height: 1.12;
  letter-spacing: 0;
}

.settings-list {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.88);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.76);
  backdrop-filter: blur(22px);
  -webkit-backdrop-filter: blur(22px);
  box-shadow: 0 18px 44px rgba(35, 42, 55, 0.1);
}

.settings-item {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 76px;
  padding: 0 16px;
  border: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: transparent;
  color: #1d1d1f;
  font: inherit;
  text-align: left;
}

.settings-item:last-child {
  border-bottom: 0;
}

.settings-item:active {
  background: rgba(0, 0, 0, 0.035);
}

.item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  margin-right: 13px;
  border-radius: 12px;
  color: #ffffff;
}

.item-icon svg {
  display: block;
  width: 22px;
  height: 22px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.item-icon-profile {
  background: #007aff;
}

.item-icon-password {
  background: #34c759;
}

.item-icon-logout {
  background: #ff3b30;
}

.item-content {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  justify-content: center;
}

.item-title {
  overflow: hidden;
  color: #1d1d1f;
  font-size: 16px;
  font-weight: 650;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-desc {
  overflow: hidden;
  margin-top: 5px;
  color: #86868b;
  font-size: 12px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chevron {
  flex: 0 0 auto;
  margin-left: 12px;
  color: #c7c7cc;
  font-size: 26px;
  font-weight: 300;
  line-height: 1;
}

@media (max-width: 380px) {
  .settings-shell {
    padding-right: 14px;
    padding-left: 14px;
  }

  .page-header h1 {
    font-size: 31px;
  }

  .settings-item {
    min-height: 72px;
    padding-right: 14px;
    padding-left: 14px;
  }
}
</style>
